import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { createOrder } from '../services/orderService';

function CheckoutPage() {
    const { cartItems, cartTotal, clearCart } = useCart();
    const history = useHistory();

    const [formData, setFormData] = useState({
        username: '',
        phoneNumber: '',
        email: '',
        street: '',
        gouvernorate: '',
        city: '',
        postalCode: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [submitMessage, setSubmitMessage] = useState('');

    const governates = [
        'Ariana', 'Béja', 'Ben Arous', 'Bizerte', 'Gabès', 'Gafsa', 'Jendouba',
        'Kairouan', 'Kasserine', 'Kébili', 'Le Kef', 'Mahdia', 'La Manouba',
        'Médenine', 'Monastir', 'Nabeul', 'Sfax', 'Sidi Bouzid', 'Siliana',
        'Sousse', 'Tataouine', 'Tozeur', 'Tunis', 'Zaghouan'
    ];

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Redirect if cart is empty
    useEffect(() => {
        if (cartItems.length === 0 && submitStatus !== 'success') {
            const timer = setTimeout(() => {
                if (submitStatus !== 'success') history.push('/shop');
            }, 3000); // Give user a moment to realize why, or just render "Cart Empty" message
            return () => clearTimeout(timer);
        }
    }, [cartItems, history, submitStatus]);


    const validateForm = () => {
        const newErrors = {};

        if (!formData.username.trim()) newErrors.username = 'Le nom est requis';
        else if (formData.username.trim().length < 2) newErrors.username = 'Le nom doit comporter au moins 2 caractères';

        const phoneRegex = /^[+]?[\d\s\-()]+$/;
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Le numéro de téléphone est requis';
        else if (!phoneRegex.test(formData.phoneNumber.trim())) newErrors.phoneNumber = 'Format de téléphone invalide';

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Format d\'email invalide';

        if (!formData.street.trim()) newErrors.street = 'L\'adresse est requise';
        if (!formData.gouvernorate) newErrors.gouvernorate = 'Le gouvernorat est requis';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const shippingFee = 9;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus(null);
        setSubmitMessage('');

        if (cartItems.length === 0) {
            setSubmitMessage('Votre panier est vide.');
            setSubmitStatus('error');
            return;
        }

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            // Map cart items to backend generic order structure (if needed, simplified here)
            // Usually CreateOrder expects a specific structure. QuickOrderForm was tailored for single product.
            // We need to check if backend supports bulk orders or creates one order per payload.
            // Assuming backend supports array of products as seen in QuickOrderForm: products: [orderProduct]

            const orderProducts = cartItems.map(item => {
                // Build options object, filtering out null, undefined, or 'Default' values
                const options = {};

                if (item.options.collection) options.collection = item.options.collection;
                if (item.options.color && item.options.color !== 'Default') options.color = item.options.color;
                if (item.options.size && item.options.size !== 'Default') options.size = item.options.size;
                if (item.options.features && Object.keys(item.options.features).length > 0) options.features = item.options.features;

                return {
                    productId: item.product.productId,
                    productName: item.product.name,
                    unitPrice: parseFloat(item.price),
                    quantity: parseInt(item.quantity, 10),
                    totalPrice: parseFloat(item.price) * parseInt(item.quantity, 10),
                    options: options,
                    images: item.options.images || []
                };
            });

            const orderPayload = {
                username: formData.username,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                address: {
                    street: formData.street,
                    gouvernorate: formData.gouvernorate,
                    city: formData.city,
                    postalCode: formData.postalCode
                },
                products: orderProducts,
                totalAmount: cartTotal
            };

            const result = await createOrder(orderPayload);

            if (result.success) {
                setSubmitStatus('success');
                setSubmitMessage(`Commande effectuée avec succès ! Numéro de commande : ${result.data.orderNumber}`);
                clearCart();
                // Redirect or show success
            } else {
                setSubmitStatus('error');
                // Filter out "failed to reduce stock" errors
                let errorMessage = result.error || 'Échec de la commande. Veuillez réessayer.';
                if (errorMessage.toLowerCase().includes('failed to reduce stock') ||
                    errorMessage.toLowerCase().includes('reduce stock')) {
                    errorMessage = 'Impossible de traiter la commande. Certains articles peuvent avoir un stock insuffisant. Veuillez vérifier votre panier et réessayer.';
                }
                setSubmitMessage(errorMessage);
            }
        } catch (err) {
            setSubmitStatus('error');
            setSubmitMessage('Une erreur inattendue s\'est produite.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (cartItems.length === 0 && submitStatus !== 'success') {
        return (
            <main>
                <section className="breadcrumb-area breadcrumb-bg" style={{ backgroundImage: 'url("img/bg/breadcrumb_bg.jpg")' }}>
                    <div className="container">
                        <div className="row">
                            <div className="col-12">
                                <div className="breadcrumb-content">
                                    <h2 className="title">Commande</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div className="container pt-100 pb-100 text-center">
                    <h3>Votre panier est vide.</h3>
                    <Link to="/shop" className="btn mt-4">Retour à la boutique</Link>
                </div>
            </main>
        )
    }

    return (
        <main>
            {/* breadcrumb-area */}
            <section className="breadcrumb-area breadcrumb-bg" style={{ backgroundImage: 'url("img/bg/breadcrumb_bg.jpg")' }}>
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="breadcrumb-content">
                                <h2 className="title">Checkout</h2>
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item"><Link to="/">Accueil</Link></li>
                                        <li className="breadcrumb-item"><Link to="/cart">Panier</Link></li>
                                        <li className="breadcrumb-item active" aria-current="page">Commande</li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* breadcrumb-area-end */}

            {/* checkout-area */}
            <section className="checkout-area pt-110 pb-110">
                <div className="container">
                    {submitStatus === 'success' ? (
                        <div className="alert alert-success text-center" role="alert">
                            <h4 className="alert-heading">Merci pour votre commande !</h4>
                            <p>{submitMessage}</p>
                            <hr />
                            <p className="mb-0">Nous vous contacterons bientôt pour confirmer votre commande.</p>
                            <Link to="/shop" className="btn mt-4">Continuer vos achats</Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-lg-7">
                                    <div className="checkout-wrap">
                                        <div className="checkout-title mb-30">
                                            <h4 className="title">Détails de facturation</h4>
                                        </div>
                                        {submitStatus === 'error' && (
                                            <div className="alert alert-danger" role="alert">
                                                {submitMessage}
                                            </div>
                                        )}
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="checkout-form-list" style={{ marginBottom: '20px' }}>
                                                    <label>Nom <span className="required">*</span></label>
                                                    <input type="text" name="username" value={formData.username} onChange={handleChange} placeholder="Votre Nom" className={`form-control ${errors.username ? 'is-invalid' : ''}`} style={{ height: '50px', backgroundColor: '#f7f7f7' }} />
                                                    {errors.username && <span className="text-danger small">{errors.username}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="checkout-form-list" style={{ marginBottom: '20px' }}>
                                                    <label>Téléphone <span className="required">*</span></label>
                                                    <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="Numéro de téléphone" className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`} style={{ height: '50px', backgroundColor: '#f7f7f7' }} />
                                                    {errors.phoneNumber && <span className="text-danger small">{errors.phoneNumber}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="checkout-form-list" style={{ marginBottom: '20px' }}>
                                                    <label>Email</label>
                                                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email (Optionnel)" className={`form-control ${errors.email ? 'is-invalid' : ''}`} style={{ height: '50px', backgroundColor: '#f7f7f7' }} />
                                                    {errors.email && <span className="text-danger small">{errors.email}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="checkout-form-list" style={{ marginBottom: '20px' }}>
                                                    <label>Adresse <span className="required">*</span></label>
                                                    <input type="text" name="street" value={formData.street} onChange={handleChange} placeholder="Adresse" className={`form-control ${errors.street ? 'is-invalid' : ''}`} style={{ height: '50px', backgroundColor: '#f7f7f7' }} />
                                                    {errors.street && <span className="text-danger small">{errors.street}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="checkout-form-list" style={{ marginBottom: '20px' }}>
                                                    <label>Gouvernorat <span className="required">*</span></label>
                                                    <select name="gouvernorate" value={formData.gouvernorate} onChange={handleChange} className={`form-control ${errors.gouvernorate ? 'is-invalid' : ''}`} style={{ height: '50px', backgroundColor: '#f7f7f7', border: '1px solid #e1e1e1' }}>
                                                        <option value="">Sélectionner...</option>
                                                        {governates.map(gov => (
                                                            <option key={gov} value={gov}>{gov}</option>
                                                        ))}
                                                    </select>
                                                    {errors.gouvernorate && <span className="text-danger small">{errors.gouvernorate}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="checkout-form-list" style={{ marginBottom: '20px' }}>
                                                    <label>Ville</label>
                                                    <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Ville" className="form-control" style={{ height: '50px', backgroundColor: '#f7f7f7' }} />
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="checkout-form-list" style={{ marginBottom: '20px' }}>
                                                    <label>Code Postal</label>
                                                    <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} placeholder="Code Postal" className="form-control" style={{ height: '50px', backgroundColor: '#f7f7f7' }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-5">
                                    <div className="shop-cart-total-order" style={{ padding: '30px', backgroundColor: '#f9f9f9', borderRadius: '5px', border: '1px solid #e1e1e1' }}>
                                        <h3 className="title mb-20">Votre Commande</h3>
                                        <div className="shop-cart-widget table-responsive">
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th className="product-name">Produit</th>
                                                        <th className="product-total">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cartItems.map(item => (
                                                        <tr className="cart_item" key={item.cartId} style={{ verticalAlign: 'middle' }}>
                                                            <td className="product-name">
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                                    <div style={{ flexShrink: 0 }}>
                                                                        <img
                                                                            src={item.options.images && item.options.images.length > 0 ? item.options.images[0] : (item.product.img || 'img/product/cart_img01.jpg')}
                                                                            alt={item.product.name}
                                                                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <h6 style={{ margin: '0 0 5px', fontSize: '14px', lineHeight: '1.2' }}>{item.product.name}</h6>
                                                                        <div style={{ fontSize: '0.85em', color: '#777', lineHeight: '1.2' }}>
                                                                            {item.options.color && <div>Couleur: {item.options.color}</div>}
                                                                            {item.options.size && <div>Taille: {item.options.size}</div>}
                                                                            <div>Qté: {item.quantity}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="product-total" style={{ verticalAlign: 'middle' }}>
                                                                <span className="amount">{(item.price * item.quantity).toFixed(2)} TND</span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="cart-subtotal">
                                                        <th>Sous-total</th>
                                                        <td><span className="amount">{cartTotal.toFixed(2)} TND</span></td>
                                                    </tr>
                                                    <tr className="shipping">
                                                        <th>Livraison</th>
                                                        <td>
                                                            <ul>
                                                                <li>
                                                                    <label>{shippingFee.toFixed(2)} TND</label>
                                                                </li>
                                                            </ul>
                                                        </td>
                                                    </tr>
                                                    <tr className="order-total">
                                                        <th><strong>Total</strong></th>
                                                        <td><strong><span className="amount">{(cartTotal + shippingFee).toFixed(2)} TND</span></strong></td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                        <div className="payment-method">
                                            <button type="submit" disabled={isSubmitting} className="btn btn-block" style={{ width: '100%' }}>
                                                {isSubmitting ? 'Traitement...' : 'Commander'}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </section>
            {/* checkout-area-end */}
        </main>
    );
}

export default CheckoutPage;
